import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {ArrowLeft, ExternalLink, TrendingDown, Search, SlidersHorizontal, RefreshCw, Cloud, Users, Calendar, DollarSign} from 'lucide-react';
import { lumi } from '../../lib/lumi';

interface Ticket {
  attraction: string;
  location: string;
  category: string;
  vendors: {
    name: string;
    price: number;
    discount?: string;
    link: string;
    rating?: number;
    lastUpdated?: string;
    availability?: 'high' | 'medium' | 'low';
  }[];
  lastFetched?: string;
  weather?: {
    condition: string;
    temperature: number;
    impactOnPrice: number;
  };
  crowdLevel?: {
    level: 'low' | 'moderate' | 'high' | 'very-high';
    waitTime: string;
  };
  peakSeason?: boolean;
}

interface ExchangeRates {
  JPY: number;
  SGD: number;
  HKD: number;
  USD: number;
}

export default function TicketDeals() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<string>('');
  const [exchangeRates, setExchangeRates] = useState<ExchangeRates | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<string>('All');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [selectedVendor, setSelectedVendor] = useState<string>('All');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 10000]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'price-low' | 'price-high' | 'discount'>('price-low');
  const [showFilters, setShowFilters] = useState(false);

  // Fetch live deals from Deno function
  const fetchDeals = async () => {
    setLoading(true);
    try {
      const result = await lumi.functions.invoke('getTravelDeals', {
        method: 'POST',
        body: {
          location: selectedLocation !== 'All' ? selectedLocation : undefined,
          category: selectedCategory !== 'All' ? selectedCategory : undefined,
        }
      });

      if (result?.data) {
        setTickets(result.data);
        setLastUpdate(result.meta?.timestamp || new Date().toISOString());
        setExchangeRates(result.meta?.exchangeRates || null);
      }
    } catch (error: any) {
      console.error('Failed to fetch deals:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch and auto-refresh every 5 minutes
  useEffect(() => {
    fetchDeals();
    const interval = setInterval(fetchDeals, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Refetch when filters change
  useEffect(() => {
    fetchDeals();
  }, [selectedLocation, selectedCategory]);

  const locations = ['All', ...Array.from(new Set(tickets.map(t => t.location)))];
  const categories = ['All', ...Array.from(new Set(tickets.map(t => t.category)))];
  const allVendors = Array.from(new Set(tickets.flatMap(t => t.vendors.map(v => v.name))));
  const vendors = ['All', ...allVendors];

  const getCurrency = (location: string) => {
    if (location.includes('Japan')) return '¥';
    if (location.includes('Singapore')) return 'S$';
    if (location.includes('Hong Kong')) return 'HK$';
    return '$';
  };

  const normalizePrice = (price: number, location: string) => {
    if (location.includes('Japan')) return price / 150;
    if (location.includes('Singapore')) return price / 1.35;
    if (location.includes('Hong Kong')) return price / 7.8;
    return price;
  };

  const filteredAndSortedTickets = useMemo(() => {
    let filtered = tickets.filter(ticket => {
      if (selectedVendor !== 'All' && !ticket.vendors.some(v => v.name === selectedVendor)) return false;
      if (searchQuery && !ticket.attraction.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      
      const minPrice = Math.min(...ticket.vendors.map(v => normalizePrice(v.price, ticket.location)));
      if (minPrice < normalizePrice(priceRange[0], ticket.location) || minPrice > normalizePrice(priceRange[1], ticket.location)) return false;
      
      return true;
    });

    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.attraction.localeCompare(b.attraction);
        case 'price-low':
          return Math.min(...a.vendors.map(v => normalizePrice(v.price, a.location))) - 
                 Math.min(...b.vendors.map(v => normalizePrice(v.price, b.location)));
        case 'price-high':
          return Math.min(...b.vendors.map(v => normalizePrice(v.price, b.location))) - 
                 Math.min(...a.vendors.map(v => normalizePrice(v.price, a.location)));
        case 'discount': {
          const getMaxDiscount = (ticket: Ticket) => {
            const discounts = ticket.vendors
              .filter(v => v.discount)
              .map(v => parseInt(v.discount?.match(/\d+/)?.[0] || '0'));
            return Math.max(...discounts, 0);
          };
          return getMaxDiscount(b) - getMaxDiscount(a);
        }
        default:
          return 0;
      }
    });
  }, [tickets, selectedVendor, priceRange, searchQuery, sortBy]);

  const resetFilters = () => {
    setSelectedLocation('All');
    setSelectedCategory('All');
    setSelectedVendor('All');
    setPriceRange([0, 10000]);
    setSearchQuery('');
    setSortBy('price-low');
  };

  const activeFilterCount = 
    (selectedLocation !== 'All' ? 1 : 0) +
    (selectedCategory !== 'All' ? 1 : 0) +
    (selectedVendor !== 'All' ? 1 : 0) +
    (searchQuery ? 1 : 0);

  const getCrowdColor = (level?: string) => {
    switch (level) {
      case 'low': return 'text-green-600 bg-green-50';
      case 'moderate': return 'text-yellow-600 bg-yellow-50';
      case 'high': return 'text-orange-600 bg-orange-50';
      case 'very-high': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getAvailabilityColor = (availability?: string) => {
    switch (availability) {
      case 'high': return 'text-green-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#FF6B35] to-[#ff8555] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/deals" className="inline-flex items-center text-gray-100 hover:text-white mb-4 transition-colors">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Deals
          </Link>
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-5xl font-bold mb-4">Live Ticket Prices</h1>
              <p className="text-xl text-gray-100">Real-time deals with weather, crowds & availability</p>
            </div>
            <button
              onClick={fetchDeals}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
          {lastUpdate && (
            <div className="mt-4 flex items-center gap-6 text-sm text-gray-200">
              <span>Last updated: {new Date(lastUpdate).toLocaleString()}</span>
              {exchangeRates && (
                <span className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Rates: ¥{exchangeRates.JPY.toFixed(2)} | S${exchangeRates.SGD.toFixed(2)} | HK${exchangeRates.HKD.toFixed(2)}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Search & Controls */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search attractions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent"
            />
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-white rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors font-medium"
          >
            <SlidersHorizontal className="w-5 h-5" />
            Filters
            {activeFilterCount > 0 && (
              <span className="bg-[#FF6B35] text-white text-xs font-bold px-2 py-1 rounded-full">
                {activeFilterCount}
              </span>
            )}
          </button>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="px-6 py-3 bg-white rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent font-medium"
          >
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
            <option value="discount">Highest Discount</option>
            <option value="name">Name A-Z</option>
          </select>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6 space-y-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-[#0A2540]">Advanced Filters</h3>
              <button
                onClick={resetFilters}
                className="text-sm text-[#FF6B35] hover:text-[#e55a2e] font-medium"
              >
                Reset All
              </button>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Location</label>
                <select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent"
                >
                  {locations.map(loc => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Category</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Vendor</label>
                <select
                  value={selectedVendor}
                  onChange={(e) => setSelectedVendor(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent"
                >
                  {vendors.map(vendor => (
                    <option key={vendor} value={vendor}>{vendor}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        <div className="text-gray-600 mb-4">
          Showing <span className="font-semibold text-[#0A2540]">{filteredAndSortedTickets.length}</span> {filteredAndSortedTickets.length === 1 ? 'result' : 'results'}
          {loading && <span className="ml-2 text-sm text-[#FF6B35]">(updating...)</span>}
        </div>
      </div>

      {/* Tickets */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {loading && tickets.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <RefreshCw className="w-12 h-12 text-[#FF6B35] animate-spin mx-auto mb-4" />
            <p className="text-xl text-gray-600">Loading live prices...</p>
          </div>
        ) : filteredAndSortedTickets.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <p className="text-xl text-gray-600 mb-4">No tickets found matching your filters</p>
            <button
              onClick={resetFilters}
              className="px-6 py-3 bg-[#FF6B35] text-white rounded-lg hover:bg-[#e55a2e] transition-colors font-medium"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {filteredAndSortedTickets.map((ticket, index) => {
              const bestDeal = ticket.vendors.reduce((min, vendor) => 
                vendor.price < min.price ? vendor : min
              , ticket.vendors[0]);

              const savings = ticket.vendors[0].price - bestDeal.price;
              const savingsPercent = Math.round((savings / ticket.vendors[0].price) * 100);

              return (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h2 className="text-2xl font-bold text-[#0A2540]">{ticket.attraction}</h2>
                        {ticket.peakSeason && (
                          <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs font-bold rounded">
                            PEAK SEASON
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-sm flex-wrap">
                        <span className="text-gray-600">{ticket.location}</span>
                        <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full font-medium">
                          {ticket.category}
                        </span>
                        {ticket.lastFetched && (
                          <span className="text-xs text-green-600 flex items-center gap-1">
                            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                            Live
                          </span>
                        )}
                      </div>
                    </div>
                    {savings > 0 && (
                      <div className="mt-4 md:mt-0 bg-green-50 px-4 py-2 rounded-lg">
                        <div className="text-sm text-gray-600">Save up to</div>
                        <div className="text-xl font-bold text-green-600">
                          {getCurrency(ticket.location)}{savings} ({savingsPercent}%)
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Real-time Data Insights */}
                  {(ticket.weather || ticket.crowdLevel) && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6 p-4 bg-gray-50 rounded-lg">
                      {ticket.weather && (
                        <div className="flex items-center gap-3">
                          <Cloud className="w-5 h-5 text-[#4A90E2]" />
                          <div>
                            <div className="text-xs text-gray-600">Weather</div>
                            <div className="font-semibold text-sm">{ticket.weather.condition} • {ticket.weather.temperature}°C</div>
                          </div>
                        </div>
                      )}
                      {ticket.crowdLevel && (
                        <div className="flex items-center gap-3">
                          <Users className="w-5 h-5 text-[#FF6B35]" />
                          <div>
                            <div className="text-xs text-gray-600">Crowd Level</div>
                            <div className={`font-semibold text-sm px-2 py-0.5 rounded inline-block ${getCrowdColor(ticket.crowdLevel.level)}`}>
                              {ticket.crowdLevel.level.toUpperCase()}
                            </div>
                          </div>
                        </div>
                      )}
                      {ticket.crowdLevel && (
                        <div className="flex items-center gap-3">
                          <Calendar className="w-5 h-5 text-[#F7B32B]" />
                          <div>
                            <div className="text-xs text-gray-600">Wait Time</div>
                            <div className="font-semibold text-sm">{ticket.crowdLevel.waitTime}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {ticket.vendors.map((vendor, i) => {
                      const isBestDeal = vendor.price === bestDeal.price;
                      return (
                        <div
                          key={i}
                          className={`relative rounded-lg border-2 p-4 transition-all ${
                            isBestDeal
                              ? 'border-[#F7B32B] bg-yellow-50 transform scale-105'
                              : 'border-gray-200 hover:border-[#4A90E2]'
                          }`}
                        >
                          {isBestDeal && (
                            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-[#F7B32B] text-white text-xs font-bold px-3 py-1 rounded-full flex items-center">
                              <TrendingDown className="w-3 h-3 mr-1" />
                              BEST DEAL
                            </div>
                          )}
                          
                          <div className="text-center mb-3">
                            <div className="text-sm font-medium text-gray-600 mb-2">{vendor.name}</div>
                            <div className="text-3xl font-bold text-[#0A2540]">
                              {getCurrency(ticket.location)}{vendor.price}
                            </div>
                            {vendor.discount && (
                              <div className="text-sm text-[#FF6B35] font-semibold mt-1">
                                {vendor.discount}
                              </div>
                            )}
                            <div className="flex items-center justify-center gap-2 mt-2 text-xs">
                              {vendor.rating && (
                                <span className="text-gray-500">
                                  ⭐ {vendor.rating.toFixed(1)}
                                </span>
                              )}
                              {vendor.availability && (
                                <span className={`font-semibold ${getAvailabilityColor(vendor.availability)}`}>
                                  • {vendor.availability.toUpperCase()}
                                </span>
                              )}
                            </div>
                          </div>

                          <a
                            href={vendor.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`block w-full text-center py-2 rounded-lg font-medium transition-colors ${
                              isBestDeal
                                ? 'bg-[#F7B32B] text-white hover:bg-[#e5a325]'
                                : 'bg-[#4A90E2] text-white hover:bg-[#3a80d2]'
                            }`}
                          >
                            <span className="flex items-center justify-center">
                              View Deal
                              <ExternalLink className="w-4 h-4 ml-2" />
                            </span>
                          </a>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
